<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {storeToRefs} from "pinia";
import {LoaderCircle, MessageSquare, QrCode, RefreshCw, Smartphone} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {Skeleton} from "@/core/components/ui/skeleton";
import Customer from "@/customer/interfaces/Customer";
import {interviewApi} from "@/container";
import {InterviewLinkQrCode} from "@/core/http/resources/InterviewResource";
import {apiErrorMessage, apiErrorStatus, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {useCreditStore} from "@/stores/credit.store";
import {useToastStore} from "@/stores/toast.store";
import dateHelper from "@/core/helpers/dateHelper";

/**
 * Hands the patient a link to an interview of the backing they fill
 * themselves: texted, or shown as a QR code to scan with their phone or a
 * tablet of the pharmacy. The link may be sent again at will: every one of
 * them resumes the same answers, until the patient sends them.
 */
const props = defineProps<{
    customer: Customer;
    backingId: number;
    interviewId: number;
}>();

const NOT_ENOUGH_CREDIT = "Crédits SMS insuffisants : rechargez votre solde pour envoyer le questionnaire.";

const toastStore = useToastStore();
const creditStore = useCreditStore();
const {credit} = storeToRefs(creditStore);

const sending = ref(false);
const qrCodeOpen = ref(false);
const qrCode = ref<InterviewLinkQrCode | null>(null);
const loadingQrCode = ref(false);

// Without a balance in store, the guard below lets the SMS through until the server refuses it.
onMounted(() => {
    if (!credit.value) {
        creditStore.refresh();
    }
});

/**
 * Why no link can be made: the link identifies the patient by their mobile,
 * and they open it with the birth date on their file.
 */
const linkUnavailableReason = computed((): string | null => {
    if (!props.customer.information?.phone) {
        return "Ajoutez un numéro de mobile au dossier du patient pour lui envoyer le questionnaire.";
    }

    if (!props.customer.information?.birth_date) {
        return "Ajoutez la date de naissance au dossier du patient : il la saisit pour ouvrir le questionnaire.";
    }

    return null;
});

/** The SMS costs a credit; the QR code sends nothing. */
const smsUnavailableReason = computed((): string | null => {
    if (linkUnavailableReason.value) {
        return linkUnavailableReason.value;
    }

    if (credit.value && credit.value.balance < 1) {
        return NOT_ENOUGH_CREDIT;
    }

    return null;
});

const sendSms = async () => {
    sending.value = true;

    try {
        await interviewApi().sendLink(props.customer.id, props.backingId, props.interviewId);
        toastStore.showSuccess({content: `Questionnaire envoyé par SMS au ${props.customer.information.phone}.`});
        creditStore.refresh();
    } catch (error) {
        if (isAuthorizationError(error)) {
            return;
        }

        // 402 carries no validation bag, so apiErrorMessage would fall back to the generic wording.
        toastStore.showError({
            content: apiErrorStatus(error) === 402 ? NOT_ENOUGH_CREDIT : apiErrorMessage(error)
        });
    } finally {
        sending.value = false;
    }
};

/** Every call mints a new link; the former ones stay valid until they expire. */
const loadQrCode = async () => {
    loadingQrCode.value = true;
    qrCode.value = null;

    try {
        qrCode.value = await interviewApi().linkQrCode(props.customer.id, props.backingId, props.interviewId);
    } catch (error) {
        qrCodeOpen.value = false;

        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }
    } finally {
        loadingQrCode.value = false;
    }
};

const openQrCode = () => {
    qrCodeOpen.value = true;
    void loadQrCode();
};
</script>

<template>
  <div class="flex items-center justify-between gap-4 rounded-lg bg-primary-lightest px-3 py-2.5">
    <p class="flex items-center gap-2.5 text-sm" :class="{'text-gray-600': smsUnavailableReason}">
      <Smartphone class="size-[18px] shrink-0 text-primary" />
      <!-- Why a link cannot go stands in for the invitation, the buttons it disables right beside it. -->
      {{ smsUnavailableReason ?? "Laissez le patient le remplir seul, sur son téléphone" }}
    </p>
    <div class="flex shrink-0 gap-1">
      <Button
        variant="outline"
        size="sm"
        class="bg-white"
        :disabled="!!smsUnavailableReason || sending"
        @click="sendSms"
      >
        <LoaderCircle v-if="sending" class="animate-spin" />
        <MessageSquare v-else />
        Envoyer par SMS
        <span class="font-normal text-gray-600">· 1 crédit</span>
      </Button>
      <Button
        variant="outline"
        size="sm"
        class="bg-white"
        :disabled="!!linkUnavailableReason"
        @click="openQrCode"
      >
        <QrCode />
        Afficher le QR code
      </Button>
    </div>

    <Dialog v-model:open="qrCodeOpen">
      <DialogContent class="max-w-sm">
        <DialogHeader>
          <DialogTitle>QR code du questionnaire</DialogTitle>
          <DialogDescription>
            À scanner avec le téléphone du patient ou une tablette de l'officine.
            Le patient confirme sa date de naissance avant de répondre.
          </DialogDescription>
        </DialogHeader>
        <Skeleton v-if="loadingQrCode || !qrCode" class="aspect-square w-full" />
        <img v-else :src="qrCode.qr_code" alt="QR code du questionnaire" class="w-full">
        <p class="text-xs text-gray-600 min-h-4">
          <template v-if="qrCode">
            Valable jusqu'au {{ dateHelper.shortFormat(qrCode.expires_at) }}
          </template>
        </p>
        <div class="flex justify-end">
          <Button variant="outline" size="sm" :disabled="loadingQrCode" @click="loadQrCode">
            <RefreshCw />
            Régénérer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>
